import React, { useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Switch } from './ui/switch';
import { cn } from '../lib/utils';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Type,
  Highlighter,
  Trash2,
  ChevronDown,
  ChevronUp,
  Upload,
  X,
  Loader2,
  ArrowUpDown
} from 'lucide-react';

// Toolbar Button Component
const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'p-2 rounded-md transition-colors',
      active ? 'bg-primary-100 text-primary-700' : 'text-neutral-600 hover:bg-neutral-100',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    {children}
  </button>
);

// Menu Bar Component
const MenuBar = ({ editor }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [showImageInput, setShowImageInput] = useState(false);
  const [showLineSpacing, setShowLineSpacing] = useState(false);

  if (!editor) return null;

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  const addImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setShowImageInput(false);
    }
  };

  const setLineSpacing = (spacing) => {
    // Apply line spacing to the editor's root element as a CSS style
    if (editor) {
      const editorElement = document.querySelector('.ProseMirror');
      if (editorElement) {
        editorElement.style.lineHeight = spacing;
      }
    }
    setShowLineSpacing(false);
  };

  return (
    <div className="border border-neutral-200 rounded-t-lg bg-neutral-50 p-2">
      <div className="flex flex-wrap items-center gap-1">
        {/* Text Style Dropdown */}
        <select
          className="h-9 px-2 py-1 text-sm border border-neutral-200 rounded-md bg-white"
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'paragraph') {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().toggleHeading({ level: parseInt(value) }).run();
            }
          }}
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' : 'paragraph'
          }
        >
          <option value="paragraph">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>

        <div className="w-px h-6 bg-neutral-200 mx-1" />

        {/* Basic Formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline"
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-neutral-200 mx-1" />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-neutral-200 mx-1" />

        {/* Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          active={editor.isActive({ textAlign: 'justify' })}
          title="Justify"
        >
          <AlignJustify className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-neutral-200 mx-1" />

        {/* Quote and Code */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-neutral-200 mx-1" />

        {/* Link */}
        <div className="relative">
          <ToolbarButton
            onClick={() => setShowLinkInput(!showLinkInput)}
            active={editor.isActive('link')}
            title="Insert Link"
          >
            <LinkIcon className="w-4 h-4" />
          </ToolbarButton>
          {showLinkInput && (
            <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 flex gap-2">
              <Input
                type="url"
                placeholder="Enter URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="w-48 h-8 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && addLink()}
              />
              <Button size="sm" onClick={addLink}>Add</Button>
              {editor.isActive('link') && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => editor.chain().focus().unsetLink().run()}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Image */}
        <div className="relative">
          <ToolbarButton
            onClick={() => setShowImageInput(!showImageInput)}
            title="Insert Image"
          >
            <ImageIcon className="w-4 h-4" />
          </ToolbarButton>
          {showImageInput && (
            <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 flex gap-2">
              <Input
                type="url"
                placeholder="Image URL"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="w-48 h-8 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && addImage()}
              />
              <Button size="sm" onClick={addImage}>Add</Button>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-neutral-200 mx-1" />

        {/* Text Color */}
        <div className="relative">
          <input
            type="color"
            className="w-8 h-8 cursor-pointer rounded border border-neutral-200"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            title="Text Color"
          />
        </div>

        {/* Highlight */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()}
          active={editor.isActive('highlight')}
          title="Highlight"
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarButton>

        {/* Line Spacing */}
        <div className="relative">
          <ToolbarButton
            onClick={() => setShowLineSpacing(!showLineSpacing)}
            title="Line Spacing"
          >
            <ArrowUpDown className="w-4 h-4" />
          </ToolbarButton>
          {showLineSpacing && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-10">
              {[
                { label: 'Single', value: '1' },
                { label: '1.15', value: '1.15' },
                { label: '1.5', value: '1.5' },
                { label: 'Double', value: '2' },
                { label: '2.5', value: '2.5' },
                { label: 'Triple', value: '3' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setLineSpacing(option.value)}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-neutral-100 whitespace-nowrap"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-neutral-200 mx-1" />

        {/* Clear Formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Clear Formatting"
        >
          <X className="w-4 h-4" />
        </ToolbarButton>
      </div>
    </div>
  );
};

// Blog Categories
const BLOG_CATEGORIES = [
  'Career Advice',
  'Industry News',
  'Job Search Tips',
  'Interview Preparation',
  'CV Writing',
  'Recruitment Trends',
  'Salary Guides',
  'Work Culture',
  'Remote Work',
  'Professional Development'
];

// Reading Time Options
const READING_TIMES = [
  '2 min read',
  '3 min read',
  '5 min read',
  '7 min read',
  '10 min read',
  '15 min read',
  '20+ min read'
];

// Main Blog Editor Component
const BlogEditor = ({ 
  initialData = null, 
  onSave, 
  onCancel,
  saving = false 
}) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    category: initialData?.category || '',
    readingTime: initialData?.reading_time || '',
    location: initialData?.location || '',
    tags: initialData?.tags || [],
    featuredImage: initialData?.featured_image || '',
    metaTitle: initialData?.meta_title || '',
    metaDescription: initialData?.meta_description || '',
    metaKeywords: initialData?.meta_keywords || '',
    canonicalUrl: initialData?.canonical_url || '',
    ogImage: initialData?.og_image || '',
    isFeatured: initialData?.is_featured || false,
    status: initialData?.status || 'draft'
  });
  
  const [tagInput, setTagInput] = useState('');
  const [seoExpanded, setSeoExpanded] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary-600 hover:text-primary-700 underline cursor-pointer'
        }
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-lg my-4'
        }
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Underline,
      Highlight.configure({
        multicolor: true
      }),
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: 'Write your blog content here...'
      })
    ],
    content: initialData?.content || '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[300px] p-4'
      }
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().replace(',', '');
      if (newTag && !formData.tags.includes(newTag) && formData.tags.length < 10) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      // Create form data for upload
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('session_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        handleInputChange('featuredImage', data.url);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = (status = 'draft') => {
    const blogData = {
      title: formData.title,
      category: formData.category,
      reading_time: formData.readingTime,
      location: formData.location,
      tags: formData.tags,
      featured_image: formData.featuredImage,
      meta_title: formData.metaTitle,
      meta_description: formData.metaDescription,
      meta_keywords: formData.metaKeywords,
      canonical_url: formData.canonicalUrl,
      og_image: formData.ogImage,
      is_featured: formData.isFeatured,
      content: editor?.getHTML() || '',
      status
    };
    onSave(blogData);
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Basic Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Blog Title */}
          <div className="md:col-span-2">
            <Label htmlFor="title" className="text-sm font-medium text-neutral-700">
              Blog Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Enter a descriptive title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Category */}
          <div>
            <Label className="text-sm font-medium text-neutral-700">
              Topic <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => handleInputChange('category', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select topic" />
              </SelectTrigger>
              <SelectContent>
                {BLOG_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reading Time */}
          <div>
            <Label className="text-sm font-medium text-neutral-700">
              Reading Time <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.readingTime}
              onValueChange={(value) => handleInputChange('readingTime', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select reading time" />
              </SelectTrigger>
              <SelectContent>
                {READING_TIMES.map(time => (
                  <SelectItem key={time} value={time}>{time}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          <div>
            <Label htmlFor="location" className="text-sm font-medium text-neutral-700">
              Location <span className="text-red-500">*</span>
            </Label>
            <Input
              id="location"
              placeholder="e.g., Global, United Kingdom, United States"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Tags */}
          <div>
            <Label htmlFor="tags" className="text-sm font-medium text-neutral-700">
              Tags
            </Label>
            <Input
              id="tags"
              placeholder="Add tags (e.g., UK, visa, immigration)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              className="mt-1"
            />
            <p className="text-xs text-neutral-500 mt-1">
              {formData.tags.length}/10 tags - Press Enter or comma to add
            </p>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Featured Image</h3>
        
        {formData.featuredImage ? (
          <div className="relative">
            <img
              src={formData.featuredImage}
              alt="Featured"
              className="w-full max-h-64 object-cover rounded-lg"
            />
            <Button
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => handleInputChange('featuredImage', '')}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {uploadingImage ? (
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
              ) : (
                <>
                  <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                  <p className="text-sm text-neutral-600">
                    <span className="font-semibold text-primary-600">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">JPEG, PNG or WebP (max 5MB)</p>
                </>
              )}
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              disabled={uploadingImage}
            />
          </label>
        )}
      </div>

      {/* Blog Content */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Blog Content</h3>
        
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <MenuBar editor={editor} />
          <div className="blog-editor-content">
            <style>{`
              .blog-editor-content .ProseMirror {
                min-height: 300px;
                padding: 1rem;
                line-height: 1.75;
              }
              .blog-editor-content .ProseMirror > * + * {
                margin-top: 0.75em;
              }
              .blog-editor-content .ProseMirror p {
                margin-bottom: 1em;
              }
              .blog-editor-content .ProseMirror h1 {
                font-size: 2em;
                font-weight: 700;
                line-height: 1.2;
                margin-top: 1.5em;
                margin-bottom: 0.5em;
              }
              .blog-editor-content .ProseMirror h2 {
                font-size: 1.5em;
                font-weight: 600;
                line-height: 1.3;
                margin-top: 1.25em;
                margin-bottom: 0.5em;
              }
              .blog-editor-content .ProseMirror h3 {
                font-size: 1.25em;
                font-weight: 600;
                line-height: 1.4;
                margin-top: 1em;
                margin-bottom: 0.5em;
              }
              .blog-editor-content .ProseMirror ul {
                list-style-type: disc;
                padding-left: 1.5em;
                margin: 1em 0;
              }
              .blog-editor-content .ProseMirror ol {
                list-style-type: decimal;
                padding-left: 1.5em;
                margin: 1em 0;
              }
              .blog-editor-content .ProseMirror li {
                margin: 0.5em 0;
              }
              .blog-editor-content .ProseMirror li p {
                margin: 0;
              }
              .blog-editor-content .ProseMirror ul ul,
              .blog-editor-content .ProseMirror ol ol,
              .blog-editor-content .ProseMirror ul ol,
              .blog-editor-content .ProseMirror ol ul {
                margin: 0.25em 0;
              }
              .blog-editor-content .ProseMirror a {
                color: #2563eb;
                text-decoration: underline;
                cursor: pointer;
                font-weight: 500;
              }
              .blog-editor-content .ProseMirror a:hover {
                color: #1d4ed8;
                text-decoration-thickness: 2px;
              }
              .blog-editor-content .ProseMirror blockquote {
                border-left: 4px solid #e5e7eb;
                padding-left: 1em;
                margin: 1em 0;
                color: #6b7280;
                font-style: italic;
              }
              .blog-editor-content .ProseMirror pre {
                background-color: #1f2937;
                color: #f3f4f6;
                padding: 1em;
                border-radius: 0.5em;
                overflow-x: auto;
                margin: 1em 0;
              }
              .blog-editor-content .ProseMirror code {
                background-color: #f3f4f6;
                padding: 0.2em 0.4em;
                border-radius: 0.25em;
                font-size: 0.9em;
              }
              .blog-editor-content .ProseMirror pre code {
                background: none;
                padding: 0;
              }
              .blog-editor-content .ProseMirror img {
                max-width: 100%;
                height: auto;
                border-radius: 0.5em;
                margin: 1em 0;
              }
              .blog-editor-content .ProseMirror mark {
                background-color: #fef08a;
                padding: 0.1em 0.2em;
                border-radius: 0.2em;
              }
              .blog-editor-content .ProseMirror hr {
                border: none;
                border-top: 2px solid #e5e7eb;
                margin: 2em 0;
              }
              .blog-editor-content .ProseMirror p.is-editor-empty:first-child::before {
                content: attr(data-placeholder);
                float: left;
                color: #9ca3af;
                pointer-events: none;
                height: 0;
              }
            `}</style>
            <EditorContent 
              editor={editor} 
              className="min-h-[300px] bg-white"
            />
          </div>
        </div>
      </div>

      {/* SEO Settings */}
      <Collapsible open={seoExpanded} onOpenChange={setSeoExpanded}>
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <CollapsibleTrigger className="flex items-center justify-between w-full p-6 hover:bg-neutral-50 transition-colors">
            <h3 className="text-lg font-semibold text-neutral-900">SEO Settings</h3>
            {seoExpanded ? (
              <ChevronUp className="w-5 h-5 text-neutral-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-neutral-500" />
            )}
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="px-6 pb-6 space-y-4 border-t border-neutral-200 pt-4">
              {/* Meta Title */}
              <div>
                <Label htmlFor="metaTitle" className="text-sm font-medium text-neutral-700">
                  Meta Title
                </Label>
                <Input
                  id="metaTitle"
                  placeholder="SEO optimized title (max 70 characters)"
                  value={formData.metaTitle}
                  onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                  maxLength={70}
                  className="mt-1"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  {formData.metaTitle.length}/70 characters
                </p>
              </div>

              {/* Meta Description */}
              <div>
                <Label htmlFor="metaDescription" className="text-sm font-medium text-neutral-700">
                  Meta Description
                </Label>
                <Textarea
                  id="metaDescription"
                  placeholder="Brief description for search engines (max 160 characters)"
                  value={formData.metaDescription}
                  onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                  maxLength={160}
                  className="mt-1"
                  rows={3}
                />
                <p className="text-xs text-neutral-500 mt-1">
                  {formData.metaDescription.length}/160 characters
                </p>
              </div>

              {/* Meta Keywords */}
              <div>
                <Label htmlFor="metaKeywords" className="text-sm font-medium text-neutral-700">
                  Meta Keywords
                </Label>
                <Input
                  id="metaKeywords"
                  placeholder="keyword1, keyword2, keyword3"
                  value={formData.metaKeywords}
                  onChange={(e) => handleInputChange('metaKeywords', e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Canonical URL */}
              <div>
                <Label htmlFor="canonicalUrl" className="text-sm font-medium text-neutral-700">
                  Canonical URL
                </Label>
                <Input
                  id="canonicalUrl"
                  placeholder="https://example.com/blog/your-post"
                  value={formData.canonicalUrl}
                  onChange={(e) => handleInputChange('canonicalUrl', e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* OG Image URL */}
              <div>
                <Label htmlFor="ogImage" className="text-sm font-medium text-neutral-700">
                  Open Graph Image URL
                </Label>
                <Input
                  id="ogImage"
                  placeholder="Image URL for social media sharing"
                  value={formData.ogImage}
                  onChange={(e) => handleInputChange('ogImage', e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Featured Toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label className="text-sm font-medium text-neutral-700">
                    Mark as Featured Blog
                  </Label>
                  <p className="text-xs text-neutral-500">
                    Featured blogs appear prominently on the homepage
                  </p>
                </div>
                <Switch
                  checked={formData.isFeatured}
                  onCheckedChange={(checked) => handleInputChange('isFeatured', checked)}
                />
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={() => handleSubmit('draft')}
          disabled={saving}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save Draft
        </Button>
        <Button
          onClick={() => handleSubmit('published')}
          disabled={saving || !formData.title || !formData.category}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Publish
        </Button>
      </div>
    </div>
  );
};

export default BlogEditor;
